// VideoTile.jsx — Video tile, talking detection, sidebar voice member, device selector.
//
// FROZEN — the ResizeObserver+opacity pattern and RMS threshold are carefully tuned.
// Never use display:none on <video> — 0x0 size stops browser decoding.
// Never simplify the ontrack new-stream-per-video-track logic in useWebRTC.js.
//
// Exports: useTalkingDetection, SidebarVoiceMember, DeviceSelector, VideoTile

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../constants';
import { rtcLog } from './CallPanel';
const VL = (msg, type = 'info') => rtcLog.push(`[video] ${msg}`, type);

// ── useTalkingDetection ───────────────────────────────────────────────────────
// FROZEN — RMS threshold 0.008, silence after 25 frames (~400ms).
export function useTalkingDetection(stream) {
  const [talking, setTalking] = useState(false);
  const animRef = useRef(null);

  useEffect(() => {
    if (!stream) { setTalking(false); return; }
    const audioTracks = stream.getAudioTracks().filter(t => t.readyState === 'live');
    if (!audioTracks.length) { setTalking(false); return; }

    let ctx, analyser, source, alive = true;
    (async () => {
      try {
        ctx = new (window.AudioContext || window.webkitAudioContext)();
        if (ctx.state === 'suspended') await ctx.resume();
        analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.4;
        source = ctx.createMediaStreamSource(stream);
        source.connect(analyser);
      } catch { return; }

      const data = new Float32Array(analyser.fftSize);
      const THRESHOLD = 0.008;
      let silence = 0;
      const tick = () => {
        if (!alive) return;
        analyser.getFloatTimeDomainData(data);
        const rms = Math.sqrt(data.reduce((s, v) => s + v * v, 0) / data.length);
        if (rms > THRESHOLD) { setTalking(true); silence = 0; }
        else if (++silence > 25) setTalking(false);
        animRef.current = requestAnimationFrame(tick);
      };
      animRef.current = requestAnimationFrame(tick);
    })();

    return () => {
      alive = false;
      cancelAnimationFrame(animRef.current);
      try { source?.disconnect(); ctx?.close(); } catch {}
      setTalking(false);
    };
  }, [stream]);

  return talking;
}

// ── SidebarVoiceMember ────────────────────────────────────────────────────────
export function SidebarVoiceMember({ member, stream, isMe }) {
  const talking = useTalkingDetection(stream);
  return (
    <div style={{ padding: '3px 10px 3px 26px', fontSize: 12, color: talking ? '#22C55E' : '#6B7280', display: 'flex', alignItems: 'center', gap: 8, transition: 'color 0.1s' }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, background: talking ? '#22C55E22' : '#1A1D24', border: talking ? '2px solid #22C55E' : '2px solid transparent', boxShadow: talking ? '0 0 6px #22C55E88' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: talking ? '#22C55E' : '#6B7280', transition: 'border 0.08s, box-shadow 0.08s' }}>
        {member.username?.slice(0, 2).toUpperCase()}
      </div>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {member.username}{isMe ? ' (you)' : ''}
      </span>
      {talking && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#22C55E', flexShrink: 0, boxShadow: '0 0 4px #22C55E' }}/>}
    </div>
  );
}

// ── useDevices (internal hook) ────────────────────────────────────────────────
function useDevices() {
  const [devices, setDevices] = useState({ audio: [], video: [] });
  useEffect(() => {
    const load = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true }).then(s => s.getTracks().forEach(t => t.stop())).catch(() => {});
        const all = await navigator.mediaDevices.enumerateDevices();
        setDevices({ audio: all.filter(d => d.kind === 'audioinput'), video: all.filter(d => d.kind === 'videoinput') });
      } catch {}
    };
    load();
    navigator.mediaDevices.addEventListener('devicechange', load);
    return () => navigator.mediaDevices.removeEventListener('devicechange', load);
  }, []);
  return devices;
}

// ── DeviceSelector / AudioVideoSettings ──────────────────────────────────────
export function DeviceSelector({ rtc, onClose }) {
  const devices = useDevices();
  const [selAudio, setSelAudio] = useState('');
  const [selVideo, setSelVideo] = useState('');
  const [tab, setTab] = useState('devices'); // 'devices' | 'audio'

  useEffect(() => {
    const stream = rtc.localStream;
    if (!stream) return;
    const at = stream.getAudioTracks()[0];
    const vt = stream.getVideoTracks()[0];
    if (at) setSelAudio(at.getSettings().deviceId || '');
    if (vt) setSelVideo(vt.getSettings().deviceId || '');
  }, [rtc.localStream]);

  const apply = async () => {
    if (selAudio) await rtc.switchDevice('audio', selAudio);
    if (selVideo) await rtc.switchDevice('video', selVideo);
    onClose();
  };

  const S = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' },
    box:     { background: '#111318', border: '1px solid #1A1D24', borderRadius: 16, width: 440, maxWidth: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' },
    label:   { fontSize: 11, fontWeight: 700, color: '#4B5563', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, display: 'block' },
    select:  { width: '100%', background: '#0D0F14', border: '1px solid #1A1D24', borderRadius: 8, color: '#CBD5E1', padding: '9px 12px', fontSize: 13, marginBottom: 16, outline: 'none' },
    btn: acc => ({ padding: '9px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: acc ? '#00D4AA' : '#1A1D24', color: acc ? '#0A0C10' : '#CBD5E1' }),
    tabBtn: active => ({ flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, background: 'transparent', color: active ? '#00D4AA' : '#4B5563', borderBottom: active ? '2px solid #00D4AA' : '2px solid transparent' }),
    slider: { width: '100%', accentColor: '#00D4AA', cursor: 'pointer' },
  };

  const peers = rtc.peers || {};
  const peerList = Object.entries(peers);

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.box} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '18px 22px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#CBD5E1' }}>🎛 Audio & Device Settings</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1A1D24', margin: '12px 0 0', padding: '0 8px' }}>
          <button style={S.tabBtn(tab === 'devices')} onClick={() => setTab('devices')}>📱 Devices</button>
          <button style={S.tabBtn(tab === 'audio')} onClick={() => setTab('audio')}>🔊 Audio Levels</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '18px 22px' }}>

          {tab === 'devices' && (
            <>
              <label style={S.label}>🎤 Microphone</label>
              <select style={S.select} value={selAudio} onChange={e => setSelAudio(e.target.value)}>
                <option value="">— Default —</option>
                {devices.audio.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${d.deviceId.slice(0, 6)}`}</option>)}
              </select>

              <label style={S.label}>📷 Camera</label>
              <select style={S.select} value={selVideo} onChange={e => setSelVideo(e.target.value)}>
                <option value="">— Default —</option>
                {devices.video.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0, 6)}`}</option>)}
              </select>

              <div style={{ fontSize: 11, color: '#4B5563', marginBottom: 16 }}>Changes apply immediately to active calls.</div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button style={S.btn(false)} onClick={onClose}>Cancel</button>
                <button style={S.btn(true)} onClick={apply}>Apply Devices</button>
              </div>
            </>
          )}

          {tab === 'audio' && (
            <>
              {/* Mic gain */}
              <label style={S.label}>🎙 Mic Input Gain</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <input type="range" min="0" max="2" step="0.05"
                  value={rtc.micGain ?? 1}
                  onChange={e => rtc.setMicGain?.(parseFloat(e.target.value))}
                  style={S.slider}/>
                <span style={{ fontSize: 13, color: '#CBD5E1', minWidth: 36, textAlign: 'right' }}>
                  {Math.round((rtc.micGain ?? 1) * 100)}%
                </span>
              </div>

              {/* Per-peer volumes */}
              <label style={S.label}>🔊 Participant Volume</label>
              {peerList.length === 0 && (
                <div style={{ fontSize: 13, color: '#4B5563', marginBottom: 16 }}>No one else is in the call.</div>
              )}
              {peerList.map(([pid, peer]) => {
                const vol = rtc.peerVolumes?.[pid] ?? 1;
                return (
                  <div key={pid} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, color: '#CBD5E1', marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                      <span>{peer.username || pid.slice(0, 8)}</span>
                      <span style={{ color: vol === 0 ? '#EF4444' : '#4B5563' }}>{vol === 0 ? '🔇' : `${Math.round(vol * 100)}%`}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button onClick={() => rtc.setPeerVolume?.(pid, vol === 0 ? 1 : 0)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0, color: vol === 0 ? '#EF4444' : '#4B5563' }}>
                        {vol === 0 ? '🔇' : '🔊'}
                      </button>
                      <input type="range" min="0" max="1" step="0.05"
                        value={vol}
                        onChange={e => rtc.setPeerVolume?.(pid, parseFloat(e.target.value))}
                        style={{ ...S.slider, flex: 1 }}/>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── VideoTile ─────────────────────────────────────────────────────────────────
// FROZEN — ResizeObserver+opacity is intentional. See file header.
export function VideoTile({ stream, label, muted: m, isLocal, expanded, onExpand, onShrink, volume }) {
  const C = useTheme();
  const ref = useRef();
  const talking = useTalkingDetection(stream);
  const [hasVideo, setHasVideo] = useState(() => !!(stream?.getVideoTracks().filter(t => t.readyState !== 'ended').length));

  const checkVideo = (s) => {
    setHasVideo(!!(s?.getVideoTracks().filter(t => t.readyState !== 'ended').length));
  };

  // Apply volume control to video element
  useEffect(() => {
    if (ref.current && volume !== undefined && !m) {
      ref.current.volume = Math.max(0, Math.min(1, volume));
    }
  }, [volume, m]);

  useEffect(() => {
    checkVideo(stream);
    if (!stream) return;

    // Poll for a few seconds after stream changes — tracks may start muted then unmute
    let tries = 0;
    const poll = setInterval(() => {
      checkVideo(stream);
      if (++tries >= 12) clearInterval(poll); // ~6 seconds
    }, 500);

    const onAdd    = () => checkVideo(stream);
    const onRemove = () => checkVideo(stream);
    stream.addEventListener('addtrack', onAdd);
    stream.addEventListener('removetrack', onRemove);

    // Also watch individual video tracks for unmute
    stream.getVideoTracks().forEach(t => { t.onunmute = () => checkVideo(stream); t.onmute = () => checkVideo(stream); });

    return () => {
      clearInterval(poll);
      stream.removeEventListener('addtrack', onAdd);
      stream.removeEventListener('removetrack', onRemove);
    };
  }, [stream]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !stream) return;

    const label = isLocal ? 'local' : 'remote';

    const resume = () => {
      const p = el.play();
      if (p) p.catch(e => VL(`play() rejected [${label}]: ${e.message}`, 'warn'));
    };

    VL(`srcObject assigned [${label}] tracks=${stream.getTracks().map(t => t.kind[0] + ':' + (t.muted ? 'M' : 'L')).join(',')}`);
    el.srcObject = null;
    el.srcObject = stream;
    resume();

    const onResize = () => {
      if (el.videoWidth > 0 && el.videoHeight > 0) {
        VL(`resize event [${label}]: ${el.videoWidth}×${el.videoHeight}`, 'ok');
        checkVideo(stream);
        resume();
      }
    };

    let tries = 0;
    const poll = setInterval(() => {
      if (el.videoWidth > 0) {
        VL(`dimensions confirmed [${label}]: ${el.videoWidth}×${el.videoHeight} (poll #${tries})`, 'ok');
        checkVideo(stream);
        clearInterval(poll);
        return;
      }
      if (++tries % 4 === 0) {
        VL(`still 0×0 [${label}] at poll #${tries} — reassigning srcObject`, 'warn');
        el.srcObject = null;
        el.srcObject = stream;
      }
      resume();
      if (tries >= 20) {
        VL(`giving up 0×0 poll [${label}] after 10s`, 'err');
        clearInterval(poll);
      }
    }, 500);

    const trackHandlers = [];
    const attachTrack = (t) => {
      const onUnmute = () => {
        VL(`track unmuted [${label}]: ${t.kind}`, 'ok');
        el.srcObject = null;
        el.srcObject = stream;
        resume();
        checkVideo(stream);
      };
      t.addEventListener('unmute', onUnmute);
      trackHandlers.push({ t, onUnmute });
    };
    stream.getTracks().forEach(attachTrack);

    const ro = new ResizeObserver(entries => {
      for (const e of entries) {
        if (e.contentRect.width > 0 && e.contentRect.height > 0) {
          if (el.srcObject !== stream) el.srcObject = stream;
          resume();
        }
      }
    });
    ro.observe(el);

    const onAdd = e => {
      VL(`addtrack [${label}]: ${e.track?.kind}`, 'ok');
      el.srcObject = null;
      el.srcObject = stream;
      resume();
      checkVideo(stream);
      if (e.track) attachTrack(e.track);
    };
    const onRemove = () => checkVideo(stream);

    const onMeta = () => VL(`loadedmetadata [${label}]: ${el.videoWidth}×${el.videoHeight}`, el.videoWidth > 0 ? 'ok' : 'warn');
    const onCanPlay = () => VL(`canplay [${label}]`);

    stream.addEventListener('addtrack', onAdd);
    stream.addEventListener('removetrack', onRemove);
    el.addEventListener('resize', onResize);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('loadedmetadata', resume);
    el.addEventListener('canplay', onCanPlay);
    el.addEventListener('canplay', resume);

    // If iOS autoplay was already unlocked (tap gate already dismissed),
    // play immediately — this tile mounted after the gate tap
    resume();

    return () => {
      clearInterval(poll);
      ro.disconnect();
      trackHandlers.forEach(({ t, onUnmute }) => t.removeEventListener('unmute', onUnmute));
      stream.removeEventListener('addtrack', onAdd);
      stream.removeEventListener('removetrack', onRemove);
      el.removeEventListener('resize', onResize);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('loadedmetadata', resume);
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('canplay', resume);
    };
  }, [stream]); // eslint-disable-line

  return (
    <div
      style={{ background: '#111318', borderRadius: 12, overflow: 'hidden', aspectRatio: expanded ? undefined : '16/9', height: expanded ? '100%' : undefined, position: 'relative', border: talking ? '3px solid #22C55E' : `1px solid ${expanded ? C.accent : C.border}`, boxShadow: talking ? '0 0 0 3px #22C55E55, 0 0 20px #22C55E44' : 'none', transition: 'border 0.08s ease, box-shadow 0.08s ease', cursor: expanded ? 'default' : 'pointer' }}
      onClick={expanded ? undefined : onExpand}
    >
      {/* Video element is ALWAYS in DOM — opacity:0 when no video (never display:none) */}
      <video ref={ref} autoPlay playsInline muted={isLocal || m}
        style={{ width: '100%', height: '100%', objectFit: expanded ? 'contain' : 'cover', display: 'block', opacity: hasVideo ? 1 : 0, position: 'absolute', top: 0, left: 0 }}
      />
      {!hasVideo && (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: talking ? '#22C55E' : C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#0A0C10', transition: 'background 0.1s' }}>
            {label?.slice(0, 2).toUpperCase()}
          </div>
        </div>
      )}
      <div style={{ position: 'absolute', bottom: 8, left: 10, right: expanded ? 36 : 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        {talking && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22C55E', flexShrink: 0, boxShadow: '0 0 6px #22C55E' }}/>}
        <div style={{ background: 'rgba(0,0,0,0.65)', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, color: talking ? '#22C55E' : '#E2E8F0', transition: 'color 0.1s' }}>{label}</div>
      </div>
      <button onClick={e => { e.stopPropagation(); expanded ? onShrink() : onExpand(); }}
        style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: 6, color: '#CBD5E1', width: 26, height: 26, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, opacity: 0.7 }}
        onMouseEnter={e => e.currentTarget.style.opacity = 1}
        onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
        ⛶
      </button>
    </div>
  );
}
